/**
 * Footer.jsx  —  Global Footer for Sagar Fashion
 *
 * USAGE
 * ─────
 * import Footer from "./components/Footer";
 *
 * // Then place it at the bottom of your App.js layout:
 * <Footer />
 */

import { Link } from "react-router-dom";

const SOCIAL = [
  {
    label: "Facebook",
    icon: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  },
  {
    label: "Instagram",
    icon: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z",
  },
  {
    label: "Twitter",
    icon: "M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z",
  },
];

const COMPANY_LINKS = [
  { label: "Contact", href: "/contact" },
  { label: "Shipping Policy", href: "/shipping-policy" },
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms & Conditions", href: "/terms" },
];

const SUPPORT_LINKS = [
  { label: "Returns", href: "/returns" },
  { label: "Contact", href: "/contact" },
];

const PAYMENT_METHODS = ["Visa", "Mastercard", "PayPal", "Apple Pay"];

const AMAZON_URL = "https://www.amazon.com/gp/aw/sp.html?seller=AHC26O5HXB60X";

export default function Footer() {
  return (
    <footer className="bg-stone-950 text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6">

        {/* Top grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-12 border-b border-white/10">

          {/* Brand — spans 2 columns */}
          <div className="md:col-span-2">
            <Link to="/" className="flex flex-col items-center leading-none mb-4 w-fit">
              <span
                className="text-white font-bold tracking-[0.15em] uppercase text-sm"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "0.18em" }}
              >
                Sagar Lifestyle NY
              </span>
              <div className="w-full flex items-center gap-1 my-[3px]">
                <div className="flex-1 h-px bg-amber-500" />
                <span className="text-amber-500 text-[9px] font-bold tracking-[0.3em] uppercase">LLC</span>
                <div className="flex-1 h-px bg-amber-500" />
              </div>
            </Link>

            <p className="text-white/50 text-sm leading-relaxed max-w-xs mb-6">
              Premium clothing and jewelry for the modern woman. Crafted with care,
              delivered with love across the United States.
            </p>

            {/* Social icons */}
            <div className="flex gap-3">
              {SOCIAL.map(({ label, icon }) => (
                <a
                  key={label}
                  href={`https://${label.toLowerCase()}.com`}
                  aria-label={label}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-rose-500 transition-colors duration-300 group"
                >
                  <svg
                    className="w-4 h-4 fill-white/70 group-hover:fill-white transition-colors duration-300"
                    viewBox="0 0 24 24"
                  >
                    <path d={icon} />
                  </svg>
                </a>
              ))}
            </div>
          </div>

          {/* Company links */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-widest text-white/40 mb-5">
              Company
            </h4>
            <ul className="flex flex-col gap-3">
              {COMPANY_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="text-white/60 text-sm hover:text-rose-400 transition-colors duration-200"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support links */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-widest text-white/40 mb-5">
              Support
            </h4>
            <ul className="flex flex-col gap-3">
              {SUPPORT_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="text-white/60 text-sm hover:text-rose-400 transition-colors duration-200"
                  >
                    {label}
                  </Link>
                </li>
              ))}

              {/* Amazon Store Button */}
              <li>
                <a
                  href={AMAZON_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-md text-xs font-bold tracking-wide transition-all duration-200"
                  style={{
                    background: "#FF9900",
                    color: "#111111",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#e68a00"}
                  onMouseLeave={e => e.currentTarget.style.background = "#FF9900"}
                >
                  {/* Amazon cart icon */}
                  <svg
                    viewBox="0 0 24 24"
                    className="w-4 h-4"
                    fill="currentColor"
                  >
                    <path d="M13.12 15.3c-2.36.58-4.93.5-7.23-.24-.37-.12-.66.26-.44.58 1.5 2.17 3.96 3.58 6.77 3.58 2.68 0 5.04-1.3 6.57-3.3.24-.32-.02-.74-.38-.62-1.73.55-3.54.84-5.29 0zM20.93 16.84c-.2-.26-.63-.14-.71.18-.5 2.05-2.12 3.71-4.17 4.27-.28.08-.38.4-.18.6.1.1.24.14.37.1 2.55-.73 4.52-2.74 5.15-5.37.04-.16-.02-.32-.13-.44-.08-.08-.2-.14-.33-.14zM7.37 4.08C5.12 5.25 3.5 7.5 3.5 10.1c0 3.86 3.13 7 7 7 .55 0 1-.45 1-1s-.45-1-1-1c-2.76 0-5-2.24-5-5 0-1.87 1.04-3.5 2.57-4.37.48-.27.65-.87.38-1.35-.27-.48-.87-.65-1.35-.38-.24.14-.47.28-.73.08zM12.5 3c-4.14 0-7.5 3.36-7.5 7.5 0 .55.45 1 1 1s1-.45 1-1C7 7.47 9.47 5 12.5 5s5.5 2.47 5.5 5.5c0 .55.45 1 1 1s1-.45 1-1C20 6.36 16.64 3 12.5 3z"/>
                  </svg>
                  Shop on Amazon
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">
            © {new Date().getFullYear()} Sagar Lifestyle NY LLC. All rights reserved.
          </p>

          {/* Amazon subtle badge */}
          <a
            href={AMAZON_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-[11px] font-semibold transition-opacity duration-200 hover:opacity-80"
            style={{ color: "#FF9900" }}
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
              <path d="M13.12 15.3c-2.36.58-4.93.5-7.23-.24-.37-.12-.66.26-.44.58 1.5 2.17 3.96 3.58 6.77 3.58 2.68 0 5.04-1.3 6.57-3.3.24-.32-.02-.74-.38-.62-1.73.55-3.54.84-5.29 0zM20.93 16.84c-.2-.26-.63-.14-.71.18-.5 2.05-2.12 3.71-4.17 4.27-.28.08-.38.4-.18.6.1.1.24.14.37.1 2.55-.73 4.52-2.74 5.15-5.37.04-.16-.02-.32-.13-.44-.08-.08-.2-.14-.33-.14zM7.37 4.08C5.12 5.25 3.5 7.5 3.5 10.1c0 3.86 3.13 7 7 7 .55 0 1-.45 1-1s-.45-1-1-1c-2.76 0-5-2.24-5-5 0-1.87 1.04-3.5 2.57-4.37.48-.27.65-.87.38-1.35-.27-.48-.87-.65-1.35-.38-.24.14-.47.28-.73.08zM12.5 3c-4.14 0-7.5 3.36-7.5 7.5 0 .55.45 1 1 1s1-.45 1-1C7 7.47 9.47 5 12.5 5s5.5 2.47 5.5 5.5c0 .55.45 1 1 1s1-.45 1-1C20 6.36 16.64 3 12.5 3z"/>
            </svg>
            Also available on Amazon
          </a>

          <div className="flex items-center gap-4">
            {PAYMENT_METHODS.map((method) => (
              <span
                key={method}
                className="text-white/20 text-xs border border-white/10 px-2 py-1 rounded"
              >
                {method}
              </span>
            ))}
          </div>
        </div>

      </div>
    </footer>
  );
}